// src/pages/auth/GoogleCallback.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Logo from '../../assets/img/roboman-logo.png';
import { useAuth } from "../../authorize/AuthContext";
import TermsPopup from "./TermsAndConditions";
import { getGoogleRedirectUri } from "./googleAuth";

const inFlightGoogleExchangeCodes = new Set();

const formatGoogleExchangeError = (message) => {
  const normalizedMessage = String(message || "");

  if (normalizedMessage.includes("invalid_grant")) {
    return "This Google sign-in link has already been used or expired. Please start Google sign-in again from the login page.";
  }

  if (normalizedMessage.includes("redirect_uri_mismatch")) {
    return "Google sign-in is misconfigured for this environment. Please verify the production redirect URI in Google Console.";
  }

  return normalizedMessage || "Google login failed";
};

const Loader = () => {
  return (
    <div className="flex justify-center">
      <div className="h-10 w-10 border-4 border-violet-500 border-t-transparent rounded-full animate-spin"></div>
    </div>
  )

}

/**
 * Flow:
 * 1) Google redirects to /auth/google/callback?code=...&state=...
 * 2) We POST { code } to backend
 * 3) Backend exchanges code -> tokens, gets userinfo, creates/links user, returns app JWT + userId + terms_accepted
 * 4) Store token + userId, then redirect (or show terms)
 */

const GoogleCallback = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const roboman_api = process.env.REACT_APP_ROBOMAN_API;

  const [statusText, setStatusText] = useState("Signing you in with Google...");
  const [errorText, setErrorText] = useState("");

  // state for term&condition popup
  const [showTermsPopup, setShowTermsPopup] = useState(false);
  const [agreeChecked, setAgreeChecked] = useState(false);


  useEffect(() => {
    const run = async () => {
      const params = new URLSearchParams(window.location.search);

      const code = params.get("code");
      const state = params.get("state"); // optional, if you also used state
      const err = params.get("error");

      if (err) {
        setErrorText(`Google Error: ${err}`);
        window.history.replaceState({}, document.title, window.location.pathname);

        return;
      }

      if (!code) {
        setErrorText("Missing 'code' in callback URL.");
        return;
      }

      // Prevent duplicate token exchanges for the same Google auth code.
      if (inFlightGoogleExchangeCodes.has(code)) {
        setStatusText("Google sign-in is already being processed...");
        return;
      }

      try {
        inFlightGoogleExchangeCodes.add(code);
        setStatusText("Verifying your Google account...");

        // IMPORTANT: this endpoint must exist in your FastAPI backend
        // Example: POST /auth/google/exchange
        // console.log("Fetching", `${roboman_api}/users/google/exchange`);
        const res = await fetch(`${roboman_api}/users/google/exchange`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            code,
            state,
            redirect_uri: getGoogleRedirectUri(),
          }),
        });

        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data?.detail || data?.message || "Google login failed");
        }

        const accessToken = data.access_token;
        const userId = data.user_id || data.userId;
        const termsAccepted =
          data.terms_and_conditions_accepted ?? data.terms_accepted;

        if (!accessToken) {
          throw new Error("Google login succeeded but no access token was returned.");
        }

        // Store in localStorage (use the same keys your app already uses)
        localStorage.setItem("access_token", accessToken);
        if (userId) localStorage.setItem("user_id", userId);

        // Clean URL so refresh doesn't re-trigger exchange
        window.history.replaceState({}, document.title, window.location.pathname);

        // If terms not accepted, redirect to your existing terms flow/page
        // If you show a popup on dashboard, you can just navigate there.
        if (termsAccepted === false) {
          // Option A: go to dashboard and it shows terms popup
          // navigate("/dashboard");
          
          setStatusText("Almost done... redirecting to complete Terms & Conditions");
          setAgreeChecked(false);
          setShowTermsPopup(true);
          return;

          // Option B: go to a dedicated page that shows TermsPopup
          // navigate("/terms");
          // return;
        }

        setStatusText("Login Success! Redirecting...");
        login();
        navigate("/dashboard");
      } catch (e) {
        console.error(e);
        setErrorText(formatGoogleExchangeError(e?.message));
      }
    };

    run();
  }, [login, navigate, roboman_api]);

  //  After agreeing to Terms
  const handleAgreeAndContinue = () => {
    // TermsPopup already called API successfully
    // Token + user_id already stored

    login();
    navigate("/dashboard");
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 ">
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-white p-8 shadow-md ">
        {/* Logo */}
        <img
          src={Logo}
          alt="Roboman"
          className="mx-auto h-6 mb-4"
        />
        <h1 className="text-xl font-semibold text-gray-800 text-center">
          Google Sign-in
        </h1>

        {!errorText ? (
          <>
            <p className="mt-2 text-sm text-gray-500 text-center">{statusText}</p>
            <Loader />
          </>

        ) : (
          <div className="mt-4 text-sm text-red-600 text-center">
            {errorText}
            <div className="mt-6">
              <button
                onClick={() => navigate("/login")}
                className="w-full rounded-lg bg-violet-600 px-4 py-2 text-white hover:bg-violet-700 transition"
              >
                Back to Login
              </button>
            </div>
          </div>
        )}
      </div>
      {showTermsPopup && (
        <TermsPopup
          agreeChecked={agreeChecked}
          setAgreeChecked={setAgreeChecked}
          onAgree={handleAgreeAndContinue}
        />
      )}

    </div>
  );
};

export default GoogleCallback;
